import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SchoolProvider } from './contexts/SchoolContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import ErrorBoundary from './components/ErrorBoundary';
import { setupGlobalErrorHandling } from './utils/validation';
import { NotificationService } from './services/NotificationService';
import RequireSchool from './components/RequireSchool';
import RequireAdmin from './components/RequireAdmin';
import WelcomePage from './components/WelcomePage';
import SignupPage from './components/SignupPage';
import LoginPage from './components/LoginPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import DashboardPage from './components/DashboardPage';
import OnboardingPage from './components/OnboardingPage';
import ProfilePage from './components/ProfilePage';
import MessagesPage from './components/messages/MessagesPage';
import CalendarPage from './components/calendar/CalendarPage';
import TasksPage from './components/tasks/TasksPage';
import DocumentsPage from './components/documents/DocumentsPage';
import SettingsPage from './components/settings/SettingsPage';
import AdminPage from './components/settings/AdminPage';
import LMSSettingsPage from './components/settings/LMSSettingsPage';
import NotificationSettingsPage from './components/settings/NotificationSettingsPage';
import CalendarSettingsPage from './components/settings/CalendarSettingsPage';
import CalendarCallback from './components/settings/CalendarCallback';
import SchoolSettingsPage from './components/settings/SchoolSettingsPage';
import AttendancePage from './components/attendance/AttendancePage';
import TeamPage from './components/team/TeamPage';
import BillingDashboard from './components/billing/BillingDashboard';
import PaymentSuccess from './components/payments/PaymentSuccess';
import PaymentCancel from './components/payments/PaymentCancel';
import PortalReturn from './components/payments/PortalReturn';
import StripeTestPage from './components/test/StripeTestPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import SupportPage from './components/SupportPage';
import GettingStartedPage from './components/GettingStartedPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  // Request notification permissions only after user is authenticated
  useEffect(() => {
    if (user) {
      NotificationService.requestNotificationPermission();
    }
  }, [user]);

  console.log('🔒 RequireAuth: Auth state', { user: user?.uid || 'null', loading });

  if (loading) {
    console.log('🔒 RequireAuth: Still loading, showing loading screen');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  if (!user) {
    console.log('🔒 RequireAuth: No user found, redirecting to /');
    return <Navigate to="/" replace />;
  }

  console.log('🔒 RequireAuth: User authenticated, proceeding to app');
  return <RequireSchool>{children}</RequireSchool>;
}

function RequireNoAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        <RequireNoAuth>
          <WelcomePage />
        </RequireNoAuth>
      } />
      <Route path="/signup" element={
        <RequireNoAuth>
          <SignupPage />
        </RequireNoAuth>
      } />
      <Route path="/login" element={
        <RequireNoAuth>
          <LoginPage />
        </RequireNoAuth>
      } />
      <Route path="/forgot-password" element={
        <RequireNoAuth>
          <ForgotPasswordPage />
        </RequireNoAuth>
      } />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/getting-started" element={<GettingStartedPage />} />

      {/* Protected routes */}
      <Route path="/onboarding" element={
        <RequireAuth>
          <OnboardingPage />
        </RequireAuth>
      } />
      <Route path="/dashboard" element={
        <RequireAuth>
          <DashboardPage />
        </RequireAuth>
      } />
      <Route path="/profile" element={
        <RequireAuth>
          <ProfilePage />
        </RequireAuth>
      } />
      <Route path="/messages" element={
        <RequireAuth>
          <MessagesPage />
        </RequireAuth>
      } />
      <Route path="/calendar" element={
        <RequireAuth>
          <CalendarPage />
        </RequireAuth>
      } />
      <Route path="/tasks" element={
        <RequireAuth>
          <TasksPage />
        </RequireAuth>
      } />
      <Route path="/documents" element={
        <RequireAuth>
          <DocumentsPage />
        </RequireAuth>
      } />
      <Route path="/team" element={
        <RequireAuth>
          <TeamPage />
        </RequireAuth>
      } />
      <Route path="/settings" element={
        <RequireAuth>
          <SettingsPage />
        </RequireAuth>
      } />
      <Route path="/settings/admin" element={
        <RequireAuth>
          <RequireAdmin>
            <AdminPage />
          </RequireAdmin>
        </RequireAuth>
      } />
      <Route path="/settings/school" element={
        <RequireAuth>
          <RequireAdmin>
            <SchoolSettingsPage />
          </RequireAdmin>
        </RequireAuth>
      } />
      <Route path="/settings/lms" element={
        <RequireAuth>
          <RequireAdmin>
            <LMSSettingsPage />
          </RequireAdmin>
        </RequireAuth>
      } />
      <Route path="/settings/notifications" element={
        <RequireAuth>
          <NotificationSettingsPage />
        </RequireAuth>
      } />
      <Route path="/settings/calendar" element={
        <RequireAuth>
          <CalendarSettingsPage />
        </RequireAuth>
      } />
      <Route path="/settings/calendar/callback" element={
        <RequireAuth>
          <CalendarCallback />
        </RequireAuth>
      } />
      <Route path="/attendance" element={
        <RequireAuth>
          <AttendancePage />
        </RequireAuth>
      } />
      <Route path="/billing" element={
        <RequireAuth>
          <BillingDashboard />
        </RequireAuth>
      } />
      <Route path="/upgrade" element={
        <RequireAuth>
          <BillingDashboard />
        </RequireAuth>
      } />
      <Route path="/payment/success" element={
        <RequireAuth>
          <PaymentSuccess />
        </RequireAuth>
      } />
      <Route path="/payment/cancel" element={
        <RequireAuth>
          <PaymentCancel />
        </RequireAuth>
      } />
      <Route path="/portal/return" element={
        <RequireAuth>
          <PortalReturn />
        </RequireAuth>
      } />
      <Route path="/test/stripe" element={
        <RequireAuth>
          <RequireAdmin>
            <StripeTestPage />
          </RequireAdmin>
        </RequireAuth>
      } />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Set up global error handling
    setupGlobalErrorHandling();
    
    // Register service worker for PWA features (can be done immediately)
    NotificationService.registerServiceWorker();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <SchoolProvider>
            <SubscriptionProvider>
              <Router>
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#4ade80',
                        secondary: '#fff',
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#fff',
                      },
                    },
                  }}
                />
                <AppRoutes />
              </Router>
            </SubscriptionProvider>
          </SchoolProvider>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;